/**
 * Worker de cómputo en el navegador del visitante (browser-compute).
 *
 * Pide unidades de trabajo a la API común (/compute/claim), las calcula con
 * Web Crypto (hoy SHA-256, no bloquea el hilo principal — digest es async/off-
 * thread) y entrega el resultado (/compute/submit). Es una MINICONTRIBUCIÓN de
 * potencia al ecosistema.
 *
 * TRANSPARENCIA (decisión de diseño, no negociable): es VISIBLE (indicador en la
 * UI) y DESACTIVABLE por el visitante (opt-out persistido). No ejecuta código
 * arbitrario: sólo operaciones whitelisteadas. No es cryptojacking.
 */
import { api } from '../api';
import { storageGet, storageRemove, storageSet } from '../safeStorage';
// jsQR se carga BAJO DEMANDA (code-split): sólo se descarga cuando llega una
// unidad `qrdecode`, para no inflar el bundle inicial de quien sólo hace sha256.
let _jsQR = null;
async function getJsQR() {
    if (!_jsQR)
        _jsQR = (await import('jsqr')).default;
    return _jsQR;
}
const CONTRIB_KEY = 'gestiora.cu'; // id anónimo del contribuyente (persistente)
const OPTOUT_KEY = 'gestiora.contribute.off'; // '1' = el visitante lo apagó
const BATCH = 4; // unidades por claim
const BUSY_MS = 250; // pausa entre lotes con trabajo (cede CPU)
const IDLE_MS = 8000; // pausa cuando no hay trabajo
const YIELD_MS = 30; // pausa entre unidades (no acaparar)
const state = { running: false, units: 0, bytes: 0, lastError: null };
const listeners = new Set();
let timer;
let started = false;
function emit() {
    for (const l of listeners)
        l({ ...state });
}
export function subscribe(l) {
    listeners.add(l);
    l({ ...state });
    return () => listeners.delete(l);
}
export function isOptedOut() {
    return storageGet(OPTOUT_KEY) === '1';
}
function contributorId() {
    let id = storageGet(CONTRIB_KEY);
    if (!id || !/^[A-Za-z0-9._-]{6,64}$/.test(id)) {
        id = `anon-${Math.random().toString(36).slice(2, 14)}`;
        storageSet(CONTRIB_KEY, id);
    }
    return id;
}
// ── base64 <-> bytes (binario seguro) ──────────────────────────────────────
function b64ToBytes(b64) {
    const bin = atob(b64);
    const buf = new ArrayBuffer(bin.length); // ArrayBuffer explícito (no Shared) para crypto.subtle
    const a = new Uint8Array(buf);
    for (let i = 0; i < bin.length; i++)
        a[i] = bin.charCodeAt(i);
    return a;
}
function bytesToB64(a) {
    let s = '';
    for (let i = 0; i < a.length; i++)
        s += String.fromCharCode(a[i]);
    return btoa(s);
}
/** Decodifica el QR de una imagen PNG (base64) → su texto, '' si no hay. */
async function qrDecode(pngB64) {
    const img = new Image();
    img.src = `data:image/png;base64,${pngB64}`;
    await img.decode();
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx)
        return '';
    ctx.drawImage(img, 0, 0);
    const data = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const jsQR = await getJsQR();
    const code = jsQR(data.data, data.width, data.height);
    return code?.data ?? '';
}
async function compute(op, inputB64) {
    if (op === 'sha256') {
        const digest = await crypto.subtle.digest('SHA-256', b64ToBytes(inputB64));
        return bytesToB64(new Uint8Array(digest));
    }
    if (op === 'qrdecode') {
        // Devuelve el TEXTO del QR (string vacío = frame sin QR; es resultado válido).
        return qrDecode(inputB64);
    }
    return null; // op no soportada por este contribuyente
}
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
async function tick() {
    if (!state.running)
        return;
    let nextMs = IDLE_MS;
    try {
        const contributor = contributorId();
        const { data } = await api.post('/compute/claim', { contributor, count: BATCH });
        const units = (data?.units ?? []);
        if (data?.accepting === false) {
            nextMs = 30000; // el servidor no acepta ahora; reintenta espaciado
        }
        else if (units.length > 0) {
            for (const u of units) {
                if (!state.running)
                    break;
                const result = await compute(u.op, u.input);
                if (result === null)
                    continue;
                await api.post('/compute/submit', { id: u.id, result, contributor });
                state.units += 1;
                state.bytes += Math.floor((u.input.length * 3) / 4);
                emit();
                await sleep(YIELD_MS);
            }
            nextMs = BUSY_MS;
        }
        state.lastError = null;
    }
    catch (e) {
        state.lastError = e instanceof Error ? e.message : String(e);
        nextMs = IDLE_MS; // backoff suave ante error de red
    }
    if (state.running)
        timer = window.setTimeout(() => void tick(), nextMs);
}
/** Arranca el worker si el visitante no se ha dado de baja. Idempotente. */
export function startContributor() {
    if (started)
        return;
    started = true;
    if (isOptedOut())
        return;
    state.running = true;
    emit();
    timer = window.setTimeout(() => void tick(), 1500); // deja cargar la página primero
}
export function pauseContributor() {
    storageSet(OPTOUT_KEY, '1');
    state.running = false;
    if (timer !== undefined)
        window.clearTimeout(timer);
    emit();
}
export function resumeContributor() {
    storageRemove(OPTOUT_KEY);
    if (!state.running) {
        state.running = true;
        emit();
        timer = window.setTimeout(() => void tick(), 200);
    }
}
